import { RootState } from '@/models/root-state.model';
import { createSelector } from 'reselect';
import { isValidEmail } from '@/utils/utils';
import { User } from '@/models/user.model';
import { getLocaleByCountryCode, isEmpty } from '@/shared/utility';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { TagResponse } from '@lucky7ventures/lucky-components';
import { WalletModel } from '@/models/gig/wallet.model';
import { FluidUserData } from '@fluidpayments/types';
import { selectFluidKycStatus } from '@/store/selectors/kycSelectors';
import { envIsCanada, envIsMexico, FRENCH_ALPHA_CODES } from '@/constants/constants';
import { selectLifetimeDepositsAmount } from '@/store/selectors/lastDepositSelectors';
import { isCuracao } from '@/utils/logic.utils';

export const selectUser = (state: RootState): User | null => state.user.user;
export const selectUserLoading = (state: RootState): boolean => state.user.userLoading;
export const selectUserTags = (state: RootState): TagResponse[] => state.user.tags;

export const selectDepositCount = (state: RootState): number | null => state.user.depositCount;
export const selectWallet = (state: RootState): WalletModel => state.user.wallet;
export const selectTagsLoaded = (state: RootState): boolean => state.user.tagsLoaded;
export const selectWalletError = (state: RootState): boolean => state.user.walletError;
export const selectWalletLoading = (state: RootState): boolean => state.user.walletLoading;

export const selectUserId = createSelector(selectUser, user => user?.UserID);

export const selectUserFirstName = createSelector(selectUser, user => user?.FirstName);
export const selectUserFullName = createSelector(selectUser, user =>
  user && user.LastName && user.FirstName ? user.FirstName + ' ' + user.LastName : '',
);
export const selectUserAlphaCode3 = createSelector(selectUser, user => user?.AlphaCode3);

export const selectUserBalance = createSelector(selectWallet, (wallet): number | null => {
  if (!wallet) {
    return null;
  }

  if (!('Balance' in wallet)) {
    return null;
  }

  return wallet.Balance;
});

// in Hygraph CMS we have an enum called PlayerTag, and all the values are uppercase + underscores instead of spaces
export const selectUserTagsAsCmsEnum = createSelector(selectUserTags, tags =>
  tags.map(tag => tag.Name.toUpperCase().replace(/\s+/g, '_')),
);

export const selectIsFastRegRest = createSelector(
  selectUserTags,
  tags => !!tags.find((tag: TagResponse) => tag.Name === 'Fastreg'),
);
export const selectUserSpecificTag = (tagName: string) =>
  createSelector(
    selectUserTags,
    tags =>
      !!tags.find(
        (tag: TagResponse) => tag.Name.toLocaleLowerCase() === tagName.toLocaleLowerCase(),
      ),
  );

export const selectIsFastRegTh = createSelector(
  selectUserTags,
  tags => !!tags.find((tag: TagResponse) => tag.Name === 'FastregTh'),
);

export const selectIsFastRegMx = createSelector(
  selectUserTags,
  tags => !!tags.find((tag: TagResponse) => tag.Name === 'Fastreg_mx'),
);

export const selectIsFastRegThDone = createSelector(
  selectUserTags,
  tags => !!tags.find((tag: TagResponse) => tag.Name === 'FastregThDone'),
);

export const selectIsFastReg = createSelector(
  selectIsFastRegRest,
  selectIsFastRegTh,
  selectIsFastRegMx,
  (fastRegRest, fastRegTh, fastRegMx) => fastRegRest || fastRegTh || fastRegMx,
);

export const selectUserRealBalance = createSelector(selectWallet, (wallet): number | null => {
  // the real balance is a calculation of Balance + RealBonusMoney across all Bonuses
  if (!wallet) {
    return null;
  }

  if (!('Balance' in wallet)) {
    return null;
  }

  const totalRealBonus = wallet.UserBonuses.reduce((accTotal, userBonus) => {
    return (
      accTotal +
      userBonus.Bonuses.reduce((accTotalBonus, bonus) => accTotalBonus + bonus.RealBonusMoney, 0)
    );
  }, 0);

  return wallet.Balance + totalRealBonus;
});

export const selectUserTotalBalance = createSelector(selectWallet, (wallet): number | null => {
  // The total balance is the sum of real Balance + all the bonuses
  if (!wallet) {
    return null;
  }

  if (!('Balance' in wallet)) {
    return null;
  }

  const totalRealBonus = wallet.UserBonuses.reduce((accTotal, userBonus) => {
    return (
      accTotal +
      userBonus.Bonuses.reduce(
        (accTotalBonus, bonus) => accTotalBonus + bonus.RealBonusMoney + bonus.BonusMoney,
        0,
      )
    );
  }, 0);

  return wallet.Balance + totalRealBonus;
});

export const selectUserLocale = createSelector(selectUser, user => {
  if (isEmpty(user) || user === null) {
    return null;
  }

  const userLocale = getLocaleByCountryCode(user.CountryCode);

  // Check if French or Canadian French
  if (userLocale === 'ca' && FRENCH_ALPHA_CODES.includes(user.LanguageAlphaCode)) {
    return 'ca-fr';
  }

  return userLocale;
});

export const selectUserCurrency = createSelector(selectUser, (user): string => {
  if (!user) {
    return '';
  }
  return user.Currency;
});

export const selectUserEmail = createSelector(selectUser, (user): string => {
  if (!user) {
    return '';
  }

  if (!isValidEmail(user.Email)) {
    return '';
  }

  return user.Email;
});

export const selectUserHasValidContactInfo = createSelector(selectUser, (user): boolean => {
  if (!user) {
    return false;
  }

  return !!user.Mobile && isValidEmail(user.Email);
});

export const selectHardReloadPending = createSelector(
  selectLocale,
  selectUserLocale,
  selectUser,
  (locale, userLocale, user) => {
    if (isEmpty(user)) {
      return false;
    }

    return locale !== userLocale;
  },
);

export const selectUserIsGameTester = createSelector(selectUserTags, tags => {
  if (tags.length === 0) {
    return false;
  }

  return tags.some(tag => tag.Name === 'Game Tester');
});

export const selectUserMigrated = createSelector(selectUserTags, tags => {
  if (tags.length === 0) {
    return false;
  }

  return tags.some(tag => tag.Name === 'DC:Migrated KYC');
});

export const selectDisableLocationSmart = createSelector(selectUserTags, tags => {
  if (tags.length === 0) {
    return false;
  }

  return tags.some(tag => tag.Name === 'Disable LocationSmart');
});

export const selectUserHasFixedDepositLimit = createSelector(selectUserTags, tags => {
  if (tags.length === 0) {
    return false;
  }

  return tags.some(tag => tag.Name === 'RG Fixed Limit');
});

export const selectUserIsFluidTester = createSelector(selectUserTags, tags => {
  if (tags.length === 0) {
    return false;
  }

  return tags.find(tag => tag.Name === 'EnableFluid');
});

export const selectUserIsRewardReelTester = createSelector(selectUserTags, tags => {
  if (tags.length === 0) {
    return false;
  }

  return !!tags.find(tag => tag.Name === 'SplashtechHidden');
});

export const selectFluidUserData = createSelector(
  selectFluidKycStatus,
  selectLifetimeDepositsAmount,
  selectLocale,
  selectUserFullName,
  (kycStatus, lifetimeDeposits, locale, fullName): FluidUserData => {
    let userData: FluidUserData = { fullName };

    // For Canada we don't need the lifetime deposit amount for the KYC flow, we just need the KYC status
    if (envIsCanada()) {
      userData.kycStatus = kycStatus;
      userData.lifetimeDeposit = 0;
    }

    if ((envIsMexico() || isCuracao(locale)) && kycStatus) {
      userData.kycStatus = lifetimeDeposits !== undefined ? kycStatus : 'unverified';
      userData.lifetimeDeposit = lifetimeDeposits || 0;
    }

    return userData;
  },
);
